import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import { 
  Users, UserPlus, Search, Filter, Mail, FileText, 
  Phone, MapPin, Calendar, DollarSign, Edit2, Trash2,
  ChevronDown, ChevronRight, Clock, CheckCircle, XCircle,
  MoreVertical, Send, Download, Plus, X, Eye, RefreshCw,
  TrendingUp, TrendingDown, AlertTriangle, BarChart3, BookOpen, FileCheck, Receipt, PenTool,
  CreditCard, Bell, FileSpreadsheet, Wallet, CalendarClock, UserCheck, Upload, Building,
  FilePlus, FileOutput, RotateCcw, GraduationCap, CalendarRange
} from 'lucide-react';
import { ALL_CRM_STATUTS, isCandidatCRM } from '../components/crm/UnifiedCRMTable';
import { UnifiedCRMTable, PaiementDialog, CRMFilters, AlertesList, CRMDashboardTab, CRMFinancesTab, CRMClientsTab, BilanDialog, ConfigDialog, ProgrammeDialog, EmailDialog, AvoirDialog, QuoteDialog, DetailsDialog, AddEditDialog, SignatureDialog, EcheancierDialog, EcheancierCreationDialog } from '../components/crm';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const CRMPage = ({ user }) => {
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [dashboard, setDashboard] = useState(null);
  const [config, setConfig] = useState({ statuts: [], taux: {}, blocs_competences: [], email_templates: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('prospects');
  const [sessionFilter, setSessionFilter] = useState(() => localStorage.getItem('crm_session_filter') || 'all');
  const [periodeFinances, setPeriodeFinances] = useState({ debut: '', fin: '' });
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showProgrammeDialog, setShowProgrammeDialog] = useState(false);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [showPaiementDialog, setShowPaiementDialog] = useState(false);
  const [showEcheancierDialog, setShowEcheancierDialog] = useState(false);
  const [showAvoirDialog, setShowAvoirDialog] = useState(false);
  
  // Signature
  const signatureRef = useRef(null);
  const [selectedDevis, setSelectedDevis] = useState(null);
  const [signatureData, setSignatureData] = useState('');
  
  // Paiements & Alertes
  const [selectedFacture, setSelectedFacture] = useState(null);
  const [alertes, setAlertes] = useState([]);
  const [statsCA, setStatsCA] = useState(null);
  const [clientsCentreFilter, setClientsCentreFilter] = useState('all'); // Filtre centre pour onglet Clients
  const [clientsSearchTerm, setClientsSearchTerm] = useState(''); // Recherche pour onglet Clients
  const [paiementData, setPaiementData] = useState({
    montant: 0,
    date_paiement: new Date().toISOString().split('T')[0],
    mode_paiement: 'virement',
    reference: '',
    echeance_id: null
  });
  const [echeancierData, setEcheancierData] = useState([]);
  // Avoir data
  const [avoirData, setAvoirData] = useState({
    montant: 0,
    motif: '',
    isTotal: false
  });
  // État pour les cartes clients dépliées/repliées
  const [expandedClients, setExpandedClients] = useState({});
  // État pour le dialog échéancier
  const [showEcheancierCreationDialog, setShowEcheancierCreationDialog] = useState(false);
  const [echeancierCreationData, setEcheancierCreationData] = useState({
    nb_echeances: 3,
    date_debut: new Date().toISOString().split('T')[0],
    montant_total: 0
  });
  
  // Bilan financier (encaissements/décaissements)
  const [bilanFinancier, setBilanFinancier] = useState({ ca_encaisse: 0, lignes: [], solde: 0 });
  const [showBilanDialog, setShowBilanDialog] = useState(false);
  const [bilanLigneData, setBilanLigneData] = useState({
    libelle: '',
    montant: 0,
    type_operation: 'decaissement',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [editingBilanLigne, setEditingBilanLigne] = useState(null);
  
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [formData, setFormData] = useState({
    nom: '', prenom: '', email: '', telephone: '', origine: '', origine_autre: '',
    adresse: '', code_postal: '', ville: '',
    statut: 'nouveau', recherche_financement: false, notes: '',
    diplome_souhaite: 'BPJEPS Activités du Motonautisme et Disciplines Associées',
    bc_selection: [], bc_intitules: {}, date_debut_souhaitee: '', mode_financement: '',
    ligne_additionnelle: '', centre: 'atlantique', envoye_par: '', entreprise_stage: '', session: ''
  });
  
  const [emailData, setEmailData] = useState({
    template_type: 'rappel_1',
    custom_subject: '',
    custom_body: '',
    preview_subject: '',
    preview_body: ''
  });
  
  // Templates d'email officiels FISL (extraits de la plateforme)
  const emailTemplates = {
    rappel_1: {
      subject: "Formation BPJEPS Motonautisme - Informations et inscription",
      body: `Bonjour {{prenom}},

Je vous écris aujourd'hui pour vous remercier de votre intérêt pour notre école et aux renseignements que vous avez sollicités concernant la formation BPJEPS Motonautisme pour devenir moniteur de jet-ski.

J'espère que les informations fournies jusqu'à présent ont répondu à vos attentes et vous ont permis de prendre une décision éclairée quant à votre participation à notre formation.

Je souhaite vous informer que toutes les dates sont sur notre site internet, pour assurer votre participation, je vous encourage vivement à procéder à votre inscription ou pré-inscription dès que possible afin de garantir la réservation de votre place aux tests de sélection.

Chez FISL, nous facilitons l'accès à l'alternance, notamment pour les candidats de moins de 25 ans, avec de nombreuses bases nautiques prêtes à proposer des contrats d'apprentissage. Pour les candidats de plus de 25 ans, différentes solutions telles que le CPF, France travail, la région, la transition professionnelle, etc., sont envisageables. Il est essentiel de ne pas attendre le dernier moment pour entamer les démarches administratives.

En choisissant de rejoindre la formation numéro un du motonautisme, vous optez pour apprendre de nouvelles compétences. Vous pouvez effectuer votre inscription directement sur notre site web www.fisl.fr ou nous contacter.

Si vous hésitez encore, je vous invite à consulter nos avis pour découvrir les témoignages vérifiés de nos anciens étudiants.

Rappelez-vous, une bonne formation n'a pas de prix. Si votre projet est toujours d'actualité, n'hésitez pas à revenir rapidement vers nous. Pour rester informé(e) sur les actualités du centre, suivez-nous sur Facebook et Instagram.

Nous sommes impatients de vous accueillir au sein de la Tribu FISL.

Cordialement,
L'équipe FISL`
    },
    rappel_2: {
      subject: "FISL - Ouverture imminente session BPJEPS Motonautisme",
      body: `Bonjour {{prenom}},

Nous avons trois excellentes nouvelles :

✔️ Ouverture imminente de notre prochaine session BPJEPS Motonautisme
✔️ Il reste des places pour les prochains tests de sélection
✔️ Il reste des opportunités en contrats d'apprentissage

Vous aviez sollicité des informations concernant la formation afin de devenir moniteur jet-ski (VNM), et nous vous remercions pour l'intérêt que vous portez à notre école.

Si votre projet est toujours d'actualité, nous vous invitons à réaliser votre inscription ou pré-inscription rapidement afin de garantir votre présence aux prochaines sélections. S'il n'est plus d'actualité, merci de nous le faire savoir.

Cordialement,
L'équipe FISL`
    },
    urgence: {
      subject: "URGENT - Dernière ligne droite pour la formation BPJEPS",
      body: `Bonjour {{prenom}},

Je me permets de revenir vers vous concernant votre intérêt pour devenir moniteur VNM. La dernière ligne droite pour s'inscrire à la formation.

Nous souhaitions savoir si vous envisagez toujours de rejoindre la prochaine session. À noter qu'à partir de l'année prochaine, les financements vont évoluer, ce qui pourrait rendre cette opportunité plus complexe à saisir.

⚠️ C'est peut-être la dernière occasion de vous lancer dans cette formation dans des conditions avantageuses.

N'hésitez pas à nous faire savoir si vous souhaitez valider votre inscription ou si vous avez des questions ?

Merci de nous dire également si votre projet est abandonné.

Dans l'attente de votre retour, je reste à votre disposition.

Cordialement,
L'équipe FISL`
    }
  };
  
  // Fonction pour générer la prévisualisation de l'email
  const generateEmailPreview = (templateType, prospect) => {
    if (templateType === 'custom') {
      return { subject: emailData.custom_subject, body: emailData.custom_body };
    }
    const template = emailTemplates[templateType];
    if (!template || !prospect) return { subject: '', body: '' };
    
    const replacePlaceholders = (text) => {
      return text
        .replace(/\{\{prenom\}\}/g, prospect.prenom || '')
        .replace(/\{\{nom\}\}/g, prospect.nom || '')
        .replace(/\{\{email\}\}/g, prospect.email || '')
        .replace(/\{\{diplome\}\}/g, prospect.diplome_souhaite || 'BPJEPS Activités du Motonautisme');
    };
    
    return {
      subject: replacePlaceholders(template.subject),
      body: replacePlaceholders(template.body)
    };
  };
  
  // État initial de quoteData pour réinitialisation
  const initialQuoteData = {
    bc_codes: '',
    bc_intitules: {},
    bc_heures: {}, // Heures par BC (ex: { BC1: 150, BC2: 200, BC3: 100 })
    date_debut: '',
    date_fin: '',
    nb_heures: 0, // Calculé automatiquement depuis bc_heures
    foad_actif: false, // Checkbox FOAD
    nb_heures_foad: 0,
    _defaultFoad: 0, // Valeur par défaut du profil centre
    duree_centre: 600,
    duree_entreprise: 450,
    taux_horaire: 0,
    taux_type: 'taux_1',
    modules_additionnels: [],
    nouveau_module: '',
    // Options supplémentaires
    test_selection: false,
    test_selection_prix: 0,
    journee_mise_niveau: 0,
    journee_mise_niveau_prix: 0,
    // Facturation
    facturation_stagiaire: { actif: true, montant: 0, adresse: '', code_postal: '', ville: '' },
    facturation_entreprise: { actif: false, nom: '', montant: 0, adresse: '', code_postal: '', ville: '' },
    facturation_financeur: { actif: false, nom: '', montant: 0, adresse: '', code_postal: '', ville: '' }
  };
  
  const [quoteData, setQuoteData] = useState(initialQuoteData);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { params: sessionFilter !== 'all' ? { session: sessionFilter } : {} };
      const [prospectsRes, statsRes, configRes, dashboardRes, alertesRes, statsCARes, bilanRes] = await Promise.all([
        axios.get('/crm/prospects'),
        axios.get('/crm/prospects/stats', params),
        axios.get('/crm/config'),
        axios.get('/crm/dashboard', params),
        axios.get('/crm/alertes', params).catch(() => ({ data: { alertes: [] } })),
        axios.get('/crm/stats-ca', params).catch(() => ({ data: null })),
        axios.get('/finances/bilan', { params: { ...params.params, ...(periodeFinances.debut ? { date_debut: periodeFinances.debut } : {}), ...(periodeFinances.fin ? { date_fin: periodeFinances.fin } : {}) } })
          .catch(() => ({ data: { ca_encaisse: 0, lignes: [], solde: 0 } }))
      ]);
      setProspects(prospectsRes.data);
      setStats(statsRes.data);
      setConfig(configRes.data);
      setDashboard(dashboardRes.data);
      setAlertes(alertesRes.data?.alertes || []);
      setStatsCA(statsCARes.data);
      setBilanFinancier(bilanRes.data || { ca_encaisse: 0, lignes: [], solde: 0 });
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [sessionFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trésorerie : rechargée seule quand la période change (sans recharger toute la page)
  useEffect(() => {
    const params = { ...(sessionFilter !== 'all' ? { session: sessionFilter } : {}),
      ...(periodeFinances.debut ? { date_debut: periodeFinances.debut } : {}), ...(periodeFinances.fin ? { date_fin: periodeFinances.fin } : {}) };
    axios.get('/finances/bilan', { params }).then(res => setBilanFinancier(res.data)).catch(() => {});
  }, [periodeFinances]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getStatusBadge = (statut) => {
    const statusInfo = config.statuts.find(s => s.value === statut) || { label: statut, color: '#6B7280' };
    return (
      <Badge 
        style={{ backgroundColor: statusInfo.color, color: 'white' }}
        className="text-xs"
      >
        {statusInfo.label}
      </Badge>
    );
  };

  const sessionsDisponibles = Array.from(new Set([
    ...prospects.map(p => p.session).filter(Boolean),
    String(new Date().getFullYear()), String(new Date().getFullYear() + 1),
  ])).sort().reverse();
  const prospectsSession = sessionFilter === 'all' ? prospects : prospects.filter(p => String(p.session) === sessionFilter);
  const changerSession = (v) => { localStorage.setItem('crm_session_filter', v); setSessionFilter(v); };

  const tabCounts = {
    prospects: prospectsSession.filter(p => ALL_CRM_STATUTS.includes(p.statut) && !isCandidatCRM(p)).length,
    candidats: prospectsSession.filter(p => ALL_CRM_STATUTS.includes(p.statut) && isCandidatCRM(p)).length,
    clients: prospectsSession.filter(p => p.statut === 'client').length,
  };
  const TabCount = ({ n, tab }) => (
    <span className={`ml-1 min-w-[22px] text-center text-xs font-semibold rounded-full px-1.5 py-0.5 ${activeTab === tab ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-700'}`}
      data-testid={`crm-tab-count-${tab}`}>{n}</span>
  );

  const filteredProspects = prospects.filter(p => {
    const matchesSearch = searchTerm === '' || 
      `${p.nom} ${p.prenom} ${p.email}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.statut === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleAddProspect = async () => {
    try {
      await axios.post('/crm/prospects', formData);
      toast.success('Prospect ajouté avec succès');
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = 'Erreur lors de l\'ajout';
      if (Array.isArray(detail)) {
        // Pydantic validation errors: format human-readable
        const fieldNames = { email: 'Email', nom: 'Nom', prenom: 'Prénom', telephone: 'Téléphone' };
        msg = detail.map(e => {
          const field = e.loc?.[e.loc.length - 1] || 'Champ';
          const niceField = fieldNames[field] || field;
          if (e.type === 'value_error' && e.msg?.includes('valid email')) return `${niceField} : adresse email invalide`;
          if (e.type === 'missing') return `${niceField} : ce champ est obligatoire`;
          return `${niceField} : ${e.msg || 'invalide'}`;
        }).join('\n');
      } else if (typeof detail === 'string') {
        msg = detail;
      } else if (error.message) {
        msg = error.message;
      }
      toast.error(msg, { duration: 6000 });
      // Log technique pour debug
      console.error('Erreur ajout prospect:', error.response?.data || error);
    }
  };

  const handleUpdateProspect = async () => {
    try {
      await axios.put(`/crm/prospects/${selectedProspect.prospect_id}`, formData);
      toast.success('Prospect mis à jour');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = 'Erreur lors de la mise à jour';
      if (Array.isArray(detail)) {
        msg = detail.map(e => `${e.loc?.[e.loc.length - 1] || 'Champ'} : ${e.msg || 'invalide'}`).join('\n');
      } else if (typeof detail === 'string') {
        msg = detail;
      }
      toast.error(msg, { duration: 6000 });
      console.error('Erreur MAJ prospect:', error.response?.data || error);
    }
  };

  const handleDeleteProspect = async (prospectId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce prospect ?')) return;
    try {
      await axios.delete(`/crm/prospects/${prospectId}`);
      toast.success('Prospect supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ============= BILAN FINANCIER FUNCTIONS =============
  const handleAddBilanLigne = async () => {
    try {
      await axios.post('/finances/bilan/ligne', bilanLigneData);
      toast.success('Ligne ajoutée au bilan');
      setShowBilanDialog(false);
      setBilanLigneData({ libelle: '', montant: 0, type_operation: 'decaissement', date: new Date().toISOString().split('T')[0], notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  const handleUpdateBilanLigne = async () => {
    if (!editingBilanLigne) return;
    try {
      await axios.put(`/finances/bilan/ligne/${editingBilanLigne}`, bilanLigneData);
      toast.success('Ligne modifiée');
      setShowBilanDialog(false);
      setEditingBilanLigne(null);
      setBilanLigneData({ libelle: '', montant: 0, type_operation: 'decaissement', date: new Date().toISOString().split('T')[0], notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const handleDeleteBilanLigne = async (ligneId) => {
    if (!window.confirm('Supprimer cette ligne ?')) return;
    try {
      await axios.delete(`/finances/bilan/ligne/${ligneId}`);
      toast.success('Ligne supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const [sendingEmail, setSendingEmail] = useState(false);
  const handleSendEmail = async () => {
    try {
      const prospect = selectedProspect;
      const email = prospect?.email || '';
      
      // Récupérer le sujet et le corps selon le type de template
      let subject, body;
      
      if (emailData.template_type === 'custom') {
        subject = emailData.custom_subject || '';
        body = emailData.custom_body || '';
      } else {
        // Utiliser la prévisualisation modifiée, ou régénérer depuis le template si vide
        if (emailData.preview_subject && emailData.preview_body) {
          subject = emailData.preview_subject;
          body = emailData.preview_body;
        } else {
          // Fallback: régénérer depuis le template
          const preview = generateEmailPreview(emailData.template_type, prospect);
          subject = preview.subject;
          body = preview.body;
        }
      }
      
      if (!email) {
        toast.error("Ce prospect n'a pas d'adresse e-mail");
        return;
      }
      setSendingEmail(true);
      await axios.post(`/crm/prospects/${prospect.prospect_id}/send-email`, {
        template_type: emailData.template_type,
        custom_subject: subject,
        custom_body: body,
        texte_brut: true,
        pieces: emailData.pieces || []
      });
      toast.success(`E-mail envoyé à ${email}`);
      setShowEmailDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "L'e-mail n'a pas pu être envoyé");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleGenerateQuote = async () => {
    // Calculer les heures totales depuis les heures par BC
    const heuresCentre = Object.values(quoteData.bc_heures || {}).reduce((sum, h) => sum + (parseInt(h) || 0), 0);
    // FOAD uniquement si activé
    const heuresFoad = quoteData.foad_actif ? (parseInt(quoteData.nb_heures_foad) || 0) : 0;
    
    // Vérifier qu'au moins quelque chose est sélectionné
    const bcSelected = quoteData.bc_codes.split(',').filter(c => c.trim()).length > 0;
    const hasTestSelection = quoteData.test_selection && quoteData.test_selection_prix > 0;
    const hasJourneesMiseNiveau = quoteData.journee_mise_niveau > 0 && quoteData.journee_mise_niveau_prix > 0;
    const hasModules = quoteData.modules_additionnels?.some(m => m.montant > 0);
    const hasFoad = quoteData.foad_actif && heuresFoad > 0;
    
    if (!bcSelected && !hasTestSelection && !hasJourneesMiseNiveau && !hasModules && heuresCentre === 0 && !hasFoad) {
      toast.error('Veuillez sélectionner au moins un élément pour le devis');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        bc_codes: quoteData.bc_codes,
        bc_intitules: JSON.stringify(quoteData.bc_intitules || {}),
        bc_heures: JSON.stringify(quoteData.bc_heures || {}),
        date_debut: quoteData.date_debut,
        date_fin: quoteData.date_fin,
        nb_heures: heuresCentre.toString(),
        nb_heures_foad: heuresFoad.toString(),
        duree_centre: (quoteData.duree_centre || 600).toString(),
        duree_entreprise: (quoteData.duree_entreprise || 450).toString(),
        taux_horaire: (quoteData.taux_horaire || 0).toString(),
        taux_type: quoteData.taux_type,
        modules_additionnels: JSON.stringify(quoteData.modules_additionnels || []),
        test_selection: quoteData.test_selection ? 'true' : 'false',
        test_selection_prix: (quoteData.test_selection_prix || 0).toString(),
        journee_mise_niveau: (quoteData.journee_mise_niveau || 0).toString(),
        journee_mise_niveau_prix: (quoteData.journee_mise_niveau_prix || 0).toString(),
        facturation: JSON.stringify({
          stagiaire: quoteData.facturation_stagiaire,
          entreprise: quoteData.facturation_entreprise,
          financeur: quoteData.facturation_financeur
        })
      });
      
      const response = await axios.get(
        `/crm/prospects/${selectedProspect.prospect_id}/generate-quote?${params.toString()}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedProspect.nom}_${selectedProspect.prenom}_devis.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Devis généré et téléchargé');
      setShowQuoteDialog(false);
      fetchData();
    } catch (error) {
      let detail = '';
      try { detail = JSON.parse(await error.response?.data?.text())?.detail; } catch (e) { detail = ''; }
      toast.error(detail || 'Erreur lors de la génération du devis');
    }
  };

  const handleGenerateProgramme = async () => {
    try {
      const response = await axios.get(
        `/crm/prospects/${selectedProspect.prospect_id}/generate-programme`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Programme_FISL_${selectedProspect.nom}_${selectedProspect.prenom}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Programme généré et téléchargé');
      setShowProgrammeDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la génération du programme');
    }
  };

  const handleSendProgramme = async () => {
    try {
      const prospect = selectedProspect;
      
      // 1. Télécharger le PDF du programme (utilise l'endpoint correct)
      const response = await axios.get(`/crm/prospects/${prospect.prospect_id}/generate-programme`, {
        responseType: 'blob'
      });
      
      // Créer le lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Programme_Formation_FISL_${prospect.nom}_${prospect.prenom}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // 2. Ouvrir la messagerie avec le texte pré-rempli
      const email = prospect?.email || '';
      const prenom = prospect?.prenom || '';
      const centreNom = prospect?.centre === 'occitanie' ? 'Centre Occitanie' : 'Centre Atlantique';
      
      const subject = encodeURIComponent(`Programme de formation BPJEPS Motonautisme - FISL`);
      const body = encodeURIComponent(`Bonjour ${prenom},

Suite à votre demande, veuillez trouver ci-joint le programme de formation BPJEPS Activités du Motonautisme et Disciplines Associées.

Cette formation vous permettra d'acquérir les compétences nécessaires pour devenir moniteur de sports nautiques motorisés.

N'hésitez pas à nous contacter pour toute question ou pour planifier un entretien.

Cordialement,
L'équipe FISL
${centreNom}
direction@fisl.fr`);
      
      // Ouvrir le client mail
      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
      
      toast.success('Programme téléchargé - Votre messagerie s\'ouvre pour l\'envoyer');
      
      // Ajouter à l'historique
      await axios.post(`/crm/prospects/${prospect.prospect_id}/send-programme-mark`);
      setShowProgrammeDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la préparation de l\'envoi');
    }
  };

  const openProgrammeDialog = (prospect) => {
    setSelectedProspect(prospect);
    setShowProgrammeDialog(true);
  };

  // ========== GESTION DEVIS ==========
  
  const handleDownloadDevis = async (prospectId, devisId) => {
    try {
      const prospect = prospects.find(p => p.prospect_id === prospectId);
      const response = await axios.get(
        `/crm/prospects/${prospectId}/devis/${devisId}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${prospect?.nom || ''}_${prospect?.prenom || ''}_devis.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Devis téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handlePreviewDevis = async (prospectId, devisId) => {
    try {
      const response = await axios.get(
        `/crm/prospects/${prospectId}/devis/${devisId}/download`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erreur lors de la prévisualisation');
    }
  };

  // Prévisualisation facture (ouvre dans un nouvel onglet)
  const handlePreviewFacture = async (prospectId, devisId, payeurKey = null) => {
    try {
      const endpoint = payeurKey 
        ? `/crm/prospects/${prospectId}/devis/${devisId}/download-facture?payeur_key=${payeurKey}`
        : `/crm/prospects/${prospectId}/factures/${devisId}/download`;
      const response = await axios.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erreur lors de la prévisualisation de la facture');
    }
  };

  // Prévisualisation avoir (ouvre dans un nouvel onglet)
  const handlePreviewAvoir = async (prospectId, devisId, avoirId) => {
    try {
      const response = await axios.get(
        `/crm/prospects/${prospectId}/devis/${devisId}/download-avoir/${avoirId}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erreur lors de la prévisualisation de l\'avoir');
    }
  };

  // Prévisualisation échéancier (ouvre dans un nouvel onglet)
  const handlePreviewEcheancier = async (prospectId, devisId, payeurKey = null) => {
    try {
      const endpoint = `/crm/prospects/${prospectId}/devis/${devisId}/download-echeancier${payeurKey ? `?payeur_key=${payeurKey}` : ''}`;
      const response = await axios.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erreur lors de la prévisualisation de l\'échéancier');
    }
  };

  const handleDownloadAllDevis = async (prospectId, devisId, isMultiPayer) => {
    try {
      // Si multi-payeurs, utiliser l'endpoint download-all qui génère un ZIP
      const endpoint = isMultiPayer 
        ? `/crm/prospects/${prospectId}/devis/${devisId}/download-all`
        : `/crm/prospects/${prospectId}/devis/${devisId}/download`;
      
      const response = await axios.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentType = response.headers['content-type'] || '';
      const extension = contentType.includes('zip') ? '.zip' : '.pdf';
      const prospect = prospects.find(p => p.prospect_id === prospectId);
      link.setAttribute('download', `${prospect?.nom || ''}_${prospect?.prenom || ''}_devis${extension}`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(isMultiPayer ? 'Devis téléchargés (ZIP)' : 'Devis téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleSendDevisByEmail = async (prospectId, devisId) => {
    try {
      // 1. Télécharger le PDF du devis
      const response = await axios.get(`/crm/prospects/${prospectId}/devis/${devisId}/download`, {
        responseType: 'blob'
      });
      
      // Créer le lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const prospect = selectedProspect;
      link.setAttribute('download', `${prospect?.nom || ''}_${prospect?.prenom || ''}_devis.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // 2. Ouvrir la messagerie avec le texte pré-rempli
      const email = prospect?.email || '';
      const prenom = prospect?.prenom || '';
      const nom = prospect?.nom || '';
      const centreNom = prospect?.centre === 'occitanie' ? 'Centre Occitanie' : 'Centre Atlantique';
      
      const subject = encodeURIComponent(`Votre devis FISL - Formation BPJEPS Motonautisme`);
      const body = encodeURIComponent(`Bonjour ${prenom},

Veuillez trouver ci-joint votre devis pour la formation BPJEPS Activités du Motonautisme et Disciplines Associées.

N'hésitez pas à nous contacter pour toute question ou pour finaliser votre inscription.

Cordialement,
L'équipe FISL
${centreNom}
direction@fisl.fr`);
      
      // Ouvrir le client mail
      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
      
      toast.success('PDF téléchargé - Votre messagerie s\'ouvre pour envoyer le devis');
      
      // Mettre à jour le statut du devis comme "envoyé"
      await axios.post(`/crm/prospects/${prospectId}/devis/${devisId}/mark-sent`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la préparation de l\'envoi');
    }
  };

  const openSignatureDialog = (prospect, devis) => {
    setSelectedProspect(prospect);
    setSelectedDevis(devis);
    setSignatureData('');
    setShowSignatureDialog(true);
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
    setSignatureData('');
  };

  const handleAcceptDevis = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error('Veuillez dessiner votre signature');
      return;
    }
    
    const signature = signatureRef.current.toDataURL();
    
    try {
      await axios.post(`/crm/prospects/${selectedProspect.prospect_id}/devis/${selectedDevis.devis_id}/accept`, null, {
        params: {
          signature: signature,
          accepte_par: `${selectedProspect.prenom} ${selectedProspect.nom}`
        }
      });
      toast.success('Devis accepté et signé !');
      setShowSignatureDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'acceptation');
    }
  };

  const handleRefuseDevis = async (prospectId, devisId) => {
    const motif = window.prompt('Motif du refus (optionnel):');
    try {
      await axios.post(`/crm/prospects/${prospectId}/devis/${devisId}/refuse`, null, {
        params: { motif: motif || '' }
      });
      toast.success('Devis refusé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du refus');
    }
  };

  // ========== GESTION FACTURES ==========

  const handleCreateFacture = async (prospectId, devisId) => {
    try {
      const response = await axios.post(`/crm/prospects/${prospectId}/devis/${devisId}/facture`);
      toast.success(`Facture ${response.data.facture_numero} créée !`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleDownloadFacture = async (prospectId, factureId) => {
    try {
      const prospect = prospects.find(p => p.prospect_id === prospectId);
      const response = await axios.get(
        `/crm/prospects/${prospectId}/factures/${factureId}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${prospect?.nom || ''}_${prospect?.prenom || ''}_facture.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Facture téléchargée');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleSendFactureByEmail = async (prospectId, factureId) => {
    try {
      // 1. Télécharger le PDF de la facture
      const response = await axios.get(`/crm/prospects/${prospectId}/factures/${factureId}/download`, {
        responseType: 'blob'
      });
      
      // Créer le lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const prospect = selectedProspect;
      link.setAttribute('download', `${prospect?.nom || ''}_${prospect?.prenom || ''}_facture.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // 2. Ouvrir la messagerie avec le texte pré-rempli
      const email = prospect?.email || '';
      const prenom = prospect?.prenom || '';
      const centreNom = prospect?.centre === 'occitanie' ? 'Centre Occitanie' : 'Centre Atlantique';
      
      const subject = encodeURIComponent(`Votre facture FISL - Formation BPJEPS Motonautisme`);
      const body = encodeURIComponent(`Bonjour ${prenom},

Veuillez trouver ci-joint votre facture pour la formation BPJEPS Activités du Motonautisme et Disciplines Associées.

N'hésitez pas à nous contacter pour toute question.

Cordialement,
L'équipe FISL
${centreNom}
direction@fisl.fr`);
      
      // Ouvrir le client mail
      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
      
      toast.success('PDF téléchargé - Votre messagerie s\'ouvre pour envoyer la facture');
      
      // Mettre à jour le statut de la facture comme "envoyée"
      await axios.post(`/crm/prospects/${prospectId}/factures/${factureId}/mark-sent`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la préparation de l\'envoi');
    }
  };

  // ========== GESTION PAIEMENTS ==========

  const openPaiementDialog = (prospect, facture) => {
    setSelectedProspect(prospect);
    setSelectedFacture(facture);
    setEditingPaiement(null);
    setEditingFacture(null);
    setPaiementData({
      montant: facture.solde_restant || facture.montant_total || 0,
      date_paiement: new Date().toISOString().split('T')[0],
      mode_paiement: 'virement',
      reference: '',
      echeance_id: null
    });
    setShowPaiementDialog(true);
  };

  const handleEnregistrerPaiement = async () => {
    try {
      const paiementBody = {
        montant: parseFloat(paiementData.montant) || 0,
        date_paiement: paiementData.date_paiement || new Date().toISOString().split('T')[0],
        mode_paiement: paiementData.mode_paiement || 'virement',
        reference: paiementData.reference || '',
        payeur_key: paiementData.payeur_key || selectedDevis?.payeur_key || null,
        echeance_id: paiementData.echeance_id || null
      };

      if (editingPaiement && selectedFacture) {
        // Mode EDITION d'un paiement existant sur facture
        const params = new URLSearchParams({
          montant: paiementBody.montant.toString(),
          date_paiement: paiementBody.date_paiement,
          mode_paiement: paiementBody.mode_paiement,
          reference: paiementBody.reference
        });
        await axios.put(
          `/crm/prospects/${selectedProspect.prospect_id}/factures/${selectedFacture.facture_id}/paiement/${editingPaiement.paiement_id}?${params.toString()}`
        );
        toast.success('Paiement modifié !');
      } else if (selectedFacture) {
        // Nouveau paiement sur facture
        const params = new URLSearchParams({
          montant: paiementBody.montant.toString(),
          date_paiement: paiementBody.date_paiement,
          mode_paiement: paiementBody.mode_paiement,
          reference: paiementBody.reference
        });
        if (paiementBody.echeance_id) {
          params.append('echeance_id', paiementBody.echeance_id);
        }
        await axios.post(
          `/crm/prospects/${selectedProspect.prospect_id}/factures/${selectedFacture.facture_id}/paiement?${params.toString()}`
        );
        toast.success('Paiement enregistré !');
      } else if (selectedDevis) {
        await axios.post(
          `/crm/prospects/${selectedProspect.prospect_id}/devis/${selectedDevis.devis_id}/paiement`,
          paiementBody
        );
        const payeurLabel = selectedDevis?.payeur_type ? ` (${selectedDevis.payeur_type})` : '';
        toast.success(`Paiement enregistré${payeurLabel} !`);
      }
      setShowPaiementDialog(false);
      setSelectedDevis(null);
      setSelectedFacture(null);
      setEditingPaiement(null);
      setEditingFacture(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  // === Edit/Delete paiement facture ===
  const [editingPaiement, setEditingPaiement] = useState(null);
  const [editingFacture, setEditingFacture] = useState(null);

  const handleEditPaiementFacture = (facture, pmt) => {
    setEditingPaiement(pmt);
    setEditingFacture(facture);
    setSelectedFacture(facture);
    setPaiementData({
      montant: pmt.montant || 0,
      date_paiement: pmt.date_paiement || new Date().toISOString().split('T')[0],
      mode_paiement: pmt.mode_paiement || 'virement',
      reference: pmt.reference || '',
      echeance_id: pmt.echeance_id || null
    });
    setShowPaiementDialog(true);
  };

  const handleDeletePaiementFacture = async (facture, pmt) => {
    if (!window.confirm(`Supprimer ce paiement de ${pmt.montant?.toLocaleString('fr-FR')} € ?`)) return;
    try {
      await axios.delete(`/crm/prospects/${selectedProspect.prospect_id}/factures/${facture.facture_id}/paiement/${pmt.paiement_id}`);
      toast.success('Paiement supprimé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEcheancierDialog = (prospect, facture) => {
    setSelectedProspect(prospect);
    setSelectedFacture(facture);
    // Initialiser avec 3 échéances par défaut
    const montant = facture.montant_total || 0;
    const today = new Date();
    setEcheancierData([
      { date_echeance: new Date(today.setMonth(today.getMonth() + 1)).toISOString().split('T')[0], montant: Math.round(montant / 3) },
      { date_echeance: new Date(today.setMonth(today.getMonth() + 1)).toISOString().split('T')[0], montant: Math.round(montant / 3) },
      { date_echeance: new Date(today.setMonth(today.getMonth() + 1)).toISOString().split('T')[0], montant: montant - 2 * Math.round(montant / 3) }
    ]);
    setShowEcheancierDialog(true);
  };

  const handleCreerEcheancier = async () => {
    try {
      await axios.post(
        `/crm/prospects/${selectedProspect.prospect_id}/factures/${selectedFacture.facture_id}/echeancier`,
        echeancierData,
        { headers: { 'Content-Type': 'application/json' } }
      );
      toast.success('Échéancier créé !');
      setShowEcheancierDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création de l\'échéancier');
    }
  };

  const handleCreerEcheancierFromCreation = async () => {
    try {
      const echeances = Array.from({ length: echeancierCreationData.nb_echeances }, (_, i) => {
        const montantEcheance = Math.round((echeancierCreationData.montant_total / echeancierCreationData.nb_echeances) * 100) / 100;
        const dateEcheance = new Date(echeancierCreationData.date_debut);
        dateEcheance.setMonth(dateEcheance.getMonth() + i);
        return {
          numero: i + 1,
          montant: montantEcheance,
          date_echeance: dateEcheance.toISOString().split('T')[0],
          statut: 'a_payer'
        };
      });
      
      await axios.post(`/crm/prospects/${selectedProspect.prospect_id}/devis/${selectedDevis.devis_id}/echeancier?payeur_key=${echeancierCreationData.payeur_key || ''}`, {
        echeances: echeances
      });
      toast.success(`Échéancier créé: ${echeancierCreationData.nb_echeances} échéances`);
      setShowEcheancierCreationDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get('/crm/export-excel', { responseType: 'blob', params: {
        ...(sessionFilter !== 'all' ? { session: sessionFilter } : {}),
        ...(periodeFinances.debut ? { date_debut: periodeFinances.debut } : {}), ...(periodeFinances.fin ? { date_fin: periodeFinances.fin } : {}) } });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export_financier_${sessionFilter !== 'all' ? `session_${sessionFilter}_` : ''}${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleStatusChange = async (prospectId, newStatus) => {
    try {
      await axios.put(`/crm/prospects/${prospectId}`, { statut: newStatus });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleUpdateTaux = async () => {
    try {
      await axios.put('/crm/config/taux', config.taux);
      toast.success('Taux mis à jour');
      setShowConfigDialog(false);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour des taux');
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '', prenom: '', email: '', telephone: '', origine: '', origine_autre: '',
      adresse: '', code_postal: '', ville: '',
      statut: 'nouveau', recherche_financement: false, notes: '',
      diplome_souhaite: 'BPJEPS Activités du Motonautisme et Disciplines Associées',
      bc_selection: [], bc_intitules: {}, date_debut_souhaitee: '', mode_financement: '',
      ligne_additionnelle: '', centre: 'atlantique', envoye_par: '', entreprise_stage: '', session: ''
    });
  };

  const openEditDialog = (prospect) => {
    setSelectedProspect(prospect);
    setFormData({
      nom: prospect.nom || '',
      prenom: prospect.prenom || '',
      email: prospect.email || '',
      telephone: prospect.telephone || '',
      origine: prospect.origine || (prospect.created_by === 'auto_inscription' ? 'inscrit_site' : ''),
      origine_autre: prospect.origine_autre || '',
      adresse: prospect.adresse || '',
      code_postal: prospect.code_postal || '',
      ville: prospect.ville || '',
      statut: prospect.statut || 'nouveau',
      recherche_financement: prospect.recherche_financement || false,
      notes: prospect.notes || '',
      diplome_souhaite: prospect.diplome_souhaite || 'BPJEPS Activités du Motonautisme et Disciplines Associées',
      bc_selection: prospect.bc_selection || [],
      date_debut_souhaitee: prospect.date_debut_souhaitee || '',
      mode_financement: prospect.mode_financement || '',
      ligne_additionnelle: prospect.ligne_additionnelle || '',
      session: prospect.session || ''
    });
    setShowEditDialog(true);
  };

  const openEmailDialog = (prospect) => {
    setSelectedProspect(prospect);
    const preview = generateEmailPreview('rappel_1', prospect);
    setEmailData({ 
      template_type: 'rappel_1', 
      custom_subject: '', 
      custom_body: '',
      preview_subject: preview.subject,
      preview_body: preview.body,
      pieces: []
    });
    setShowEmailDialog(true);
  };

  const openQuoteDialog = async (prospect) => {
    setSelectedProspect(prospect);
    
    // Charger les heures par défaut du centre depuis le profil
    const centreKey = prospect.centre || 'atlantique';
    const siteKey = centreKey.charAt(0).toUpperCase() + centreKey.slice(1);
    let defaultHours = { bc1: '', bc2: '', bc3: '' };
    let defaultBcNames = { BC1: '', BC2: '', BC3: '' };
    let defaultFoad = 0;
    let defaultDureeCentre = 600;
    let defaultDureeStage = 450;
    let defaultTauxHoraire = 0;
    let tauxApprentissage = 0;
    
    try {
      const profileRes = await axios.get('/centre/profiles');
      // Support nouveau format avec sites
      let siteProfile = null;
      if (profileRes.data && profileRes.data.sites) {
        siteProfile = profileRes.data.sites.find(s => 
          s.site_name?.toLowerCase() === centreKey.toLowerCase() || 
          s.site_id?.toLowerCase() === centreKey.toLowerCase()
        );
      } else if (profileRes.data && profileRes.data[siteKey]) {
        siteProfile = profileRes.data[siteKey];
      }
      
      if (siteProfile) {
        defaultHours = {
          bc1: siteProfile.heures_bc1 || '',
          bc2: siteProfile.heures_bc2 || '',
          bc3: siteProfile.heures_bc3 || ''
        };
        defaultBcNames = {
          BC1: siteProfile.nom_bc1 || '',
          BC2: siteProfile.nom_bc2 || '',
          BC3: siteProfile.nom_bc3 || ''
        };
        defaultFoad = parseInt(siteProfile.heures_foad) || 0;
        defaultDureeCentre = parseInt(siteProfile.duree_totale_centre) || 600;
        defaultDureeStage = parseInt(siteProfile.duree_totale_stage) || 450;
        defaultTauxHoraire = parseFloat(siteProfile.taux_horaire_formation) || 0;
        tauxApprentissage = parseFloat(siteProfile.taux_horaire_apprentissage) || 0;
      }
    } catch (error) {
    }

    // PIF du candidat : heures nettes (allégements déduits) pré-remplies et BC cochés
    let pif = null;
    try {
      pif = (await axios.get(`/crm/prospects/${prospect.prospect_id}/heures-pif`)).data;
    } catch (error) {
    }
    const pifBcs = pif ? Object.entries(pif.bc_heures).filter(([, h]) => h > 0) : [];
    
    // Réinitialisation complète avec les valeurs du prospect
    // NOTE: Les heures par défaut sont stockées mais NE SONT PAS comptées dans le total
    // tant que le BC n'est pas coché (bc_codes vide)
    // On stocke les heures par défaut dans _defaultHours pour les utiliser au moment du coche
    setQuoteData({
      ...initialQuoteData,
      bc_codes: pifBcs.map(([code]) => code).join(','),
      bc_intitules: defaultBcNames,
      bc_heures: Object.fromEntries(pifBcs),
      _defaultHours: pif ? pif.bc_heures : {
        BC1: defaultHours.bc1,
        BC2: defaultHours.bc2,
        BC3: defaultHours.bc3
      },
      _defaultFoad: pif?.a_foad ? pif.foad : defaultFoad,
      _depuisPif: pif ? { allegements: pif.allegements } : null,
      date_debut: prospect.date_debut_souhaitee || '',
      date_fin: '',
      nb_heures: 0,
      foad_actif: !!(pif?.a_foad && pif.foad > 0),
      nb_heures_foad: pif?.a_foad ? pif.foad : defaultFoad,
      duree_centre: defaultDureeCentre,
      duree_entreprise: defaultDureeStage,
      taux_horaire: prospect.mode_financement === 'cfa' && tauxApprentissage ? tauxApprentissage : defaultTauxHoraire,
      _tauxFormation: defaultTauxHoraire,
      _tauxApprentissage: tauxApprentissage,
      taux_type: prospect.mode_financement === 'cfa' ? 'taux_2' : 'taux_1',
      modules_additionnels: prospect.ligne_additionnelle ? [{ intitule: prospect.ligne_additionnelle, montant: 0 }] : [],
      nouveau_module: '',
      test_selection: false,
      test_selection_prix: 0,
      journee_mise_niveau: 0,
      journee_mise_niveau_prix: 0,
      facturation_stagiaire: { 
        actif: true, 
        montant: 0, 
        adresse: prospect.adresse || '', 
        code_postal: prospect.code_postal || '', 
        ville: prospect.ville || '' 
      },
      facturation_entreprise: { actif: false, nom: '', montant: 0, adresse: '', code_postal: '', ville: '' },
      facturation_financeur: { actif: false, nom: '', montant: 0, adresse: '', code_postal: '', ville: '' }
    });
    setShowQuoteDialog(true);
  };

  const openDetailsDialog = (prospect) => {
    setSelectedProspect(prospect);
    setShowDetailsDialog(true);
  };

  // Handler pour générer un avoir
  const handleGenerateAvoir = async () => {
    try {
      const response = await axios.post(`/crm/prospects/${selectedProspect.prospect_id}/devis/${selectedDevis.devis_id}/generate-avoir`, null, {
        params: {
          montant: avoirData.montant,
          motif: avoirData.motif || `Avoir partiel ${selectedDevis.payeur_type || ''}`,
          is_total: false,
          payeur_key: selectedDevis.payeur_key || avoirData.payeur_key
        }
      });
      toast.success(`Avoir généré: ${response.data.avoir_numero}`);
      setShowAvoirDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-[#8DC63F]" />
      </div>
    );
  }

  // Handlers pour le nouvel onglet Dashboard
  const handleViewDormantProspect = (email) => {
    setActiveTab('prospects');
    setSearchTerm(email);
  };

  const handleEditBilanLigneFromTab = (ligne) => {
    setBilanLigneData({
      libelle: ligne.libelle,
      montant: ligne.montant,
      type_operation: ligne.type_operation,
      date: ligne.date,
      notes: ligne.notes || ''
    });
    setEditingBilanLigne(ligne.ligne_id);
    setShowBilanDialog(true);
  };

  return (
    <div className="space-y-6" data-testid="crm-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Business</h1>
          <p className="text-slate-500 text-sm">Gestion des prospects et suivi commercial</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={sessionFilter} onValueChange={changerSession}>
            <SelectTrigger className="w-[190px]" data-testid="crm-session-filter">
              <CalendarRange className="h-4 w-4 mr-2 text-slate-500" />
              <SelectValue placeholder="Session" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="crm-session-option-all">Toutes les sessions</SelectItem>
              {sessionsDisponibles.map(s => (
                <SelectItem key={s} value={s} data-testid={`crm-session-option-${s}`}>Session {s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={() => setShowConfigDialog(true)}
            data-testid="config-btn"
          >
            <DollarSign className="h-4 w-4 mr-2" />
            Taux
          </Button>
          <Button 
            onClick={() => { resetForm(); setShowAddDialog(true); }}
            className="bg-[#8DC63F] hover:bg-[#7AB62F]"
            data-testid="add-prospect-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Nouveau prospect
          </Button>
        </div>
      </div>

      {/* Tabs for Dashboard / Prospects / Candidats / Clients / Finances */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full grid-cols-2 ${user?.role === 'centre' ? 'sm:grid-cols-5' : 'sm:grid-cols-3'} mb-4 h-auto p-1 gap-1`}>
          <TabsTrigger value="dashboard" className="flex items-center gap-2 data-[state=active]:bg-slate-800 data-[state=active]:text-white">
            <BarChart3 className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="prospects" className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Users className="h-4 w-4" />
            Prospects
            <TabCount n={tabCounts.prospects} tab="prospects" />
          </TabsTrigger>
          <TabsTrigger value="candidats" className="flex items-center gap-2 data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="crm-tab-candidats">
            <GraduationCap className="h-4 w-4" />
            Candidats
            <TabCount n={tabCounts.candidats} tab="candidats" />
          </TabsTrigger>
          {user?.role === 'centre' && (
            <TabsTrigger value="clients" className="flex items-center gap-2 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <CheckCircle className="h-4 w-4" />
              Clients
              <TabCount n={tabCounts.clients} tab="clients" />
            </TabsTrigger>
          )}
          {user?.role === 'centre' && (
            <TabsTrigger value="finances" className="flex items-center gap-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <Wallet className="h-4 w-4" />
              Finances
              {alertes.length > 0 && (
                <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5">{alertes.length}</span>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <CRMDashboardTab 
            dashboard={dashboard}
            config={config}
            onViewProspect={handleViewDormantProspect}
          />
        </TabsContent>

        {/* Prospects / Candidats Tabs - Unified CRM Table */}
        {['prospects', 'candidats'].map(mode => (
          <TabsContent key={mode} value={mode}>
            <UnifiedCRMTable
              mode={mode}
              prospects={prospectsSession}
              config={config}
              onViewDetails={openDetailsDialog}
              onEdit={openEditDialog}
              onEmail={openEmailDialog}
              onQuote={openQuoteDialog}
              onProgramme={openProgrammeDialog}
              onEnregistrerPaiement={(prospect, devis) => {
                setSelectedProspect(prospect);
                setSelectedDevis(devis);
                setPaiementData({
                  montant: devis.montant_total || 0,
                  date_paiement: new Date().toISOString().split('T')[0],
                  mode_paiement: 'virement',
                  reference: '',
                  echeance_id: null
                });
                setShowPaiementDialog(true);
              }}
              fetchData={fetchData}
              onValidateClient={() => {
                // Changer automatiquement vers l'onglet Clients après validation
                setActiveTab('clients');
              }}
            />
          </TabsContent>
        ))}

        {/* Clients Tab (Centre only) - Vue des clients basculés avec factures */}
        {user?.role === 'centre' && (
          <TabsContent value="clients">
            <CRMClientsTab
              prospects={prospectsSession}
              clientsCentreFilter={clientsCentreFilter}
              setClientsCentreFilter={setClientsCentreFilter}
              clientsSearchTerm={clientsSearchTerm}
              setClientsSearchTerm={setClientsSearchTerm}
              expandedClients={expandedClients}
              setExpandedClients={setExpandedClients}
              setSelectedDevis={setSelectedDevis}
              setSelectedProspect={setSelectedProspect}
              setShowPaiementDialog={setShowPaiementDialog}
              setPaiementData={setPaiementData}
              setShowAvoirDialog={setShowAvoirDialog}
              setAvoirData={setAvoirData}
              handlePreviewDevis={handlePreviewDevis}
              handlePreviewFacture={handlePreviewFacture}
              handlePreviewAvoir={handlePreviewAvoir}
              handlePreviewEcheancier={handlePreviewEcheancier}
              fetchData={fetchData}
              openDetailsDialog={openDetailsDialog}
              openQuoteDialog={openQuoteDialog}
              setEcheancierCreationData={setEcheancierCreationData}
              setShowEcheancierCreationDialog={setShowEcheancierCreationDialog}
            />
          </TabsContent>
        )}

        {/* Finances Tab */}
        {user?.role === 'centre' && (
          <TabsContent value="finances">
            <CRMFinancesTab
              statsCA={statsCA}
              bilanFinancier={bilanFinancier}
              alertes={alertes}
              onAddBilanLigne={handleAddBilanLigne}
              onEditBilanLigne={handleEditBilanLigneFromTab}
              onDeleteBilanLigne={handleDeleteBilanLigne}
              onExportExcel={handleExportExcel}
              session={sessionFilter}
              sessions={sessionsDisponibles}
              onSessionChange={changerSession}
              periode={periodeFinances}
              onPeriodeChange={setPeriodeFinances}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* Add/Edit Dialog - Composant refactorisé */}
      <AddEditDialog
        open={showAddDialog || showEditDialog}
        onOpenChange={(open) => { 
          if (!open) { setShowAddDialog(false); setShowEditDialog(false); }
        }}
        isEdit={showEditDialog}
        formData={formData}
        setFormData={setFormData}
        config={config}
        onSave={showEditDialog ? handleUpdateProspect : handleAddProspect}
        onCancel={() => { setShowAddDialog(false); setShowEditDialog(false); }}
      />

      {/* Email Dialog */}
      <EmailDialog
        open={showEmailDialog}
        onOpenChange={setShowEmailDialog}
        selectedProspect={selectedProspect}
        emailData={emailData}
        setEmailData={setEmailData}
        config={config}
        generateEmailPreview={generateEmailPreview}
        onSendEmail={handleSendEmail}
        sending={sendingEmail}
      />

      <QuoteDialog
        open={showQuoteDialog}
        onOpenChange={setShowQuoteDialog}
        selectedProspect={selectedProspect}
        quoteData={quoteData}
        setQuoteData={setQuoteData}
        config={config}
        onGenerateQuote={handleGenerateQuote}
      />

      {/* Details Dialog - Composant refactorisé */}
      <DetailsDialog
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
        selectedProspect={selectedProspect}
        getStatusBadge={getStatusBadge}
        // Callbacks pour devis
        onDeleteDevis={async (devisId, statut) => {
          const signe = statut === 'signe' || statut === 'accepte';
          if (window.confirm(`${signe ? 'Ce devis est signé. ' : ''}Supprimer définitivement ce devis ? Les factures liées seront aussi supprimées.`)) {
            try {
              await axios.delete(`/crm/prospects/${selectedProspect.prospect_id}/devis/${devisId}`);
              toast.success('Devis supprimé');
              fetchData();
              const res = await axios.get(`/crm/prospects/${selectedProspect.prospect_id}`);
              setSelectedProspect(res.data);
            } catch (err) {
              toast.error('Erreur lors de la suppression');
            }
          }
        }}
        onPreviewDevis={(devisId) => handlePreviewDevis(selectedProspect?.prospect_id, devisId)}
        onDownloadDevis={(devisId) => handleDownloadDevis(selectedProspect?.prospect_id, devisId)}
        onSendDevisByEmail={(devisId) => handleSendDevisByEmail(selectedProspect?.prospect_id, devisId)}
        onCreateFacture={(devisId) => handleCreateFacture(selectedProspect?.prospect_id, devisId)}
        onDownloadAllDevis={(devisId, isMultiPayer) => handleDownloadAllDevis(selectedProspect?.prospect_id, devisId, isMultiPayer)}
        onRefuseDevis={(devisId) => handleRefuseDevis(selectedProspect?.prospect_id, devisId)}
        onPaiementDevis={(devis) => {
          setSelectedDevis(devis);
          setShowPaiementDialog(true);
        }}
        onPaiementPayeur={(devis, payeur) => {
          setSelectedDevis(devis);
          setPaiementData({
            montant: payeur.montant,
            date_paiement: new Date().toISOString().split('T')[0],
            mode_paiement: 'virement',
            reference: '',
            payer_type: payeur.type.toLowerCase(),
            echeance_id: null
          });
          setShowPaiementDialog(true);
        }}
        // Callbacks pour factures
        onPreviewFacture={(factureId) => handlePreviewFacture(selectedProspect?.prospect_id, factureId)}
        onDownloadFacture={(factureId) => handleDownloadFacture(selectedProspect?.prospect_id, factureId)}
        onSendFactureByEmail={(factureId) => handleSendFactureByEmail(selectedProspect?.prospect_id, factureId)}
        onPaiementFacture={(facture) => openPaiementDialog(selectedProspect, facture)}
        onEcheancierFacture={(facture) => openEcheancierDialog(selectedProspect, facture)}
        onEditPaiementFacture={(facture, pmt) => handleEditPaiementFacture(facture, pmt)}
        onDeletePaiementFacture={(facture, pmt) => handleDeletePaiementFacture(facture, pmt)}
        // Actions rapides
        onSendEmail={() => { setShowDetailsDialog(false); openEmailDialog(selectedProspect); }}
        onGenerateQuote={() => { setShowDetailsDialog(false); openQuoteDialog(selectedProspect); }}
        onEdit={() => { setShowDetailsDialog(false); openEditDialog(selectedProspect); }}
      />

      {/* Config Dialog */}
      <ConfigDialog
        open={showConfigDialog}
        onOpenChange={setShowConfigDialog}
        config={config}
        setConfig={setConfig}
        onSave={handleUpdateTaux}
      />

      {/* Programme Dialog */}
      <ProgrammeDialog
        open={showProgrammeDialog}
        onOpenChange={setShowProgrammeDialog}
        selectedProspect={selectedProspect}
        onSendProgramme={handleSendProgramme}
        onDownloadProgramme={handleGenerateProgramme}
      />

      {/* Signature Dialog - Composant refactorisé */}
      <SignatureDialog
        ref={signatureRef}
        open={showSignatureDialog}
        onOpenChange={setShowSignatureDialog}
        selectedDevis={selectedDevis}
        selectedProspect={selectedProspect}
        onClear={clearSignature}
        onAccept={handleAcceptDevis}
        onCancel={() => setShowSignatureDialog(false)}
      />

      {/* Paiement Dialog */}
      <Dialog open={showPaiementDialog} onOpenChange={setShowPaiementDialog}>
        <DialogContent className="max-w-md" data-testid="paiement-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-green-600" />
              {editingPaiement ? 'Modifier le paiement' : 'Enregistrer un paiement'}
            </DialogTitle>
            <DialogDescription>
              {selectedDevis?.payeur_type && (
                <span className="font-medium text-slate-700">{selectedDevis.payeur_type} - </span>
              )}
              {selectedFacture ? `Facture: ${selectedFacture.facture_numero}` : selectedDevis ? `Facture: ${selectedDevis.facture_numero || selectedDevis.devis_id}` : ''}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="flex justify-between">
                <span>Montant {selectedDevis?.payeur_type || 'total'}:</span>
                <span className="font-bold">{(selectedDevis?.payeur_montant || selectedFacture?.montant_total || selectedDevis?.montant_total || 0).toLocaleString('fr-FR')} €</span>
              </div>
              <div className="flex justify-between text-green-600">
                <span>Déjà payé:</span>
                <span>{(() => {
                  const payeurKey = selectedDevis?.payeur_key;
                  const paiements = selectedFacture?.paiements || selectedDevis?.paiements || [];
                  const payeurPaiements = payeurKey ? paiements.filter(p => p.payeur_key === payeurKey || !p.payeur_key) : paiements;
                  return payeurPaiements.reduce((s, p) => s + (p.montant || 0), 0).toLocaleString('fr-FR');
                })()} €</span>
              </div>
              <div className="flex justify-between text-orange-600 font-medium">
                <span>Reste à payer:</span>
                <span>{(() => {
                  const total = selectedDevis?.payeur_montant || selectedFacture?.montant_total || selectedDevis?.montant_total || 0;
                  const payeurKey = selectedDevis?.payeur_key;
                  const paiements = selectedFacture?.paiements || selectedDevis?.paiements || [];
                  const payeurPaiements = payeurKey ? paiements.filter(p => p.payeur_key === payeurKey || !p.payeur_key) : paiements;
                  const paye = payeurPaiements.reduce((s, p) => s + (p.montant || 0), 0);
                  return (total - paye).toLocaleString('fr-FR');
                })()} €</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Montant</Label>
                <Input
                  type="number"
                  value={paiementData.montant}
                  onChange={(e) => setPaiementData({...paiementData, montant: parseFloat(e.target.value) || 0})}
                  data-testid="paiement-montant"
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={paiementData.date_paiement}
                  onChange={(e) => setPaiementData({...paiementData, date_paiement: e.target.value})}
                  data-testid="paiement-date"
                />
              </div>
            </div>
            
            <div>
              <Label>Mode de paiement</Label>
              <Select value={paiementData.mode_paiement} onValueChange={(v) => setPaiementData({...paiementData, mode_paiement: v})}>
                <SelectTrigger data-testid="paiement-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="virement">Virement bancaire</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="cb">Carte bancaire</SelectItem>
                  <SelectItem value="especes">Espèces</SelectItem>
                  <SelectItem value="cpf">CPF</SelectItem>
                  <SelectItem value="opco">OPCO</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Référence (optionnel)</Label>
              <Input
                placeholder="N° de chèque, virement..."
                value={paiementData.reference}
                onChange={(e) => setPaiementData({...paiementData, reference: e.target.value})}
                data-testid="paiement-reference"
              />
            </div>
            
            {/* Sélection échéance si applicable - depuis selectedDevis ou selectedFacture */}
            {(() => {
              // Récupérer les échéances depuis selectedDevis ou selectedFacture
              const payeurKey = selectedDevis?.payeur_key;
              let echeances = [];
              
              if (selectedDevis?.echeancier) {
                // Échéancier peut être un objet par payeur ou un tableau
                if (Array.isArray(selectedDevis.echeancier)) {
                  echeances = selectedDevis.echeancier.filter(e => !payeurKey || e.payeur_key === payeurKey || !e.payeur_key);
                } else if (typeof selectedDevis.echeancier === 'object' && payeurKey && selectedDevis.echeancier[payeurKey]) {
                  echeances = selectedDevis.echeancier[payeurKey] || [];
                }
              } else if (selectedFacture?.echeancier) {
                echeances = selectedFacture.echeancier;
              }
              
              // Filtrer les échéances non payées
              const echeancesNonPayees = echeances.filter(e => e.statut !== 'paye' && e.statut !== 'payee');
              
              if (echeancesNonPayees.length === 0) return null;
              
              return (
                <div>
                  <Label>Payer une échéance</Label>
                  <Select 
                    value={paiementData.echeance_id || 'none'} 
                    onValueChange={(v) => {
                      if (v === 'none') {
                        setPaiementData({...paiementData, echeance_id: null});
                      } else {
                        // Trouver l'échéance et pré-remplir le montant
                        const ech = echeancesNonPayees.find(e => e.echeance_id === v);
                        setPaiementData({
                          ...paiementData, 
                          echeance_id: v,
                          montant: ech?.montant || paiementData.montant
                        });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir une échéance" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Paiement libre (sans échéance)</SelectItem>
                      {echeancesNonPayees.map((ech, idx) => (
                        <SelectItem key={ech.echeance_id || idx} value={ech.echeance_id || `ech-${idx}`}>
                          #{ech.numero || idx + 1} - {new Date(ech.date_echeance).toLocaleDateString('fr-FR')} - {ech.montant?.toLocaleString('fr-FR')} €
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPaiementDialog(false); setEditingPaiement(null); }}>Annuler</Button>
            <Button onClick={handleEnregistrerPaiement} className="bg-green-600 hover:bg-green-700" data-testid="confirm-paiement-btn">
              <CheckCircle className="h-4 w-4 mr-2" />
              {editingPaiement ? 'Modifier' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Échéancier Dialog - Composant refactorisé */}
      <EcheancierDialog
        open={showEcheancierDialog}
        onOpenChange={setShowEcheancierDialog}
        selectedFacture={selectedFacture}
        echeancierData={echeancierData}
        setEcheancierData={setEcheancierData}
        onConfirm={handleCreerEcheancier}
        onCancel={() => setShowEcheancierDialog(false)}
      />

      <AvoirDialog
        open={showAvoirDialog}
        onOpenChange={setShowAvoirDialog}
        selectedDevis={selectedDevis}
        avoirData={avoirData}
        setAvoirData={setAvoirData}
        onGenerateAvoir={handleGenerateAvoir}
      />

      {/* Dialog Créer Échéancier - Composant refactorisé */}
      <EcheancierCreationDialog
        open={showEcheancierCreationDialog}
        onOpenChange={setShowEcheancierCreationDialog}
        selectedDevis={selectedDevis}
        echeancierCreationData={echeancierCreationData}
        setEcheancierCreationData={setEcheancierCreationData}
        onConfirm={handleCreerEcheancierFromCreation}
        onCancel={() => setShowEcheancierCreationDialog(false)}
      />

      {/* Dialog Ajout/Modification Ligne Bilan */}
      <BilanDialog
        open={showBilanDialog}
        onOpenChange={setShowBilanDialog}
        bilanLigneData={bilanLigneData}
        setBilanLigneData={setBilanLigneData}
        editingBilanLigne={editingBilanLigne}
        onSave={editingBilanLigne ? handleUpdateBilanLigne : handleAddBilanLigne}
        onCancel={() => setShowBilanDialog(false)}
      />
    </div>
  );
};

export default CRMPage;
